/**
 * @file Contexte d'authentification : qui est connecté, et fin de session.
 * @module packages.common.contexts.auth-context.AuthProvider
 *
 * L'état est restauré depuis le stockage local au montage, puis confirmé auprès
 * du serveur. Cette confirmation ne peut jamais fermer la session par elle-même :
 * seul un refus explicite du serveur le fait, via l'intercepteur Axios. Une
 * vérification qui échoue pour cause de réseau ou de backend indisponible laisse
 * la session telle quelle et sera retentée.
 */

import React, {useState, useCallback, useEffect, useRef, useMemo} from 'react';
import {AuthContext} from './AuthContext';
import {useLogout, useVerifyToken} from 'packages/common/hooks/api/auth/Auth';
import {terminateSession} from 'packages/common/utils/sessionTermination';
import {AUTH_STATE_KEY} from 'packages/common/utils/sessionStorageKeys';
import {TERMINATION_REASON} from 'packages/common/stores/sessionStatus';

// Seuil de fraicheur : ne re-verifier au focus que si > 15 minutes
const FOCUS_VERIFY_THRESHOLD = 15 * 60 * 1000;

const INITIAL_AUTH_STATE = {
  isAuthenticated: false,
  userRole: null,
  userId: null,
  emailVerified: false,
  loading: false,
};

export const AuthProvider = ({children}) => {
  const [authState, setAuthState] = useState(() => {
    const storedAuthState = localStorage.getItem(AUTH_STATE_KEY);
    return storedAuthState ? JSON.parse(storedAuthState) : INITIAL_AUTH_STATE;
  });

  const lastVerifyRef = useRef(0);
  const isVerifyingRef = useRef(false);

  const logoutHook = useLogout();
  const verifyToken = useVerifyToken();

  /**
   * Enregistre la session établie. N'a qu'un sens : l'utilisateur est connecté,
   * voici qui il est. Fermer une session passe par `logout`, jamais par ici.
   */
  const setAuthInfo = useCallback(({userId, role, emailVerified}) => {
    const newState = {
      isAuthenticated: true,
      userRole: role,
      userId,
      emailVerified,
      loading: false,
    };
    localStorage.setItem(AUTH_STATE_KEY, JSON.stringify(newState));
    setAuthState(newState);
  }, []);

  const logout = useCallback(() => {
    // Nettoyage serveur en tâche de fond : la session locale ne dépend pas de sa réussite.
    logoutHook();

    // Désactiver l'authentification en premier empêche le SessionProvider de
    // réinitialiser une session pendant le nettoyage.
    setAuthState(INITIAL_AUTH_STATE);

    // Le nettoyage de tout ce qui est persisté vit dans un seul endroit. Le
    // motif compte : un départ volontaire n'a pas à ramener la personne
    // suivante sur la page de celle qui part. La redirection est laissée aux
    // guards, qui renvoient vers la connexion dès que l'état cesse d'être
    // authentifié.
    terminateSession({reason: TERMINATION_REASON.LOGOUT});
  }, [logoutHook]);

  // Confirmation de la session auprès du serveur, au plus une fois par seuil de
  // fraicheur. L'horodatage n'est retenu qu'en cas de succès : un échec passager
  // doit pouvoir être retenté au prochain focus plutôt qu'attendre le seuil.
  const verifyIfStale = useCallback(async () => {
    const now = Date.now();

    if (isVerifyingRef.current || now - lastVerifyRef.current < FOCUS_VERIFY_THRESHOLD) {
      return;
    }

    try {
      isVerifyingRef.current = true;
      await verifyToken();
      lastVerifyRef.current = Date.now();
    } catch (error) {
      // Un refus avere a deja ete traite par l'intercepteur. Ce qui arrive ici
      // est passager : la session reste ouverte et sera reconfirmee plus tard.
    } finally {
      isVerifyingRef.current = false;
    }
  }, [verifyToken]);

  // Verification initiale au mount si authentifie
  useEffect(() => {
    if (authState.isAuthenticated) {
      verifyIfStale();
    }
  }, [authState.isAuthenticated, verifyIfStale]);

  // Verification au focus de fenetre (seulement si stale > 15 min)
  useEffect(() => {
    const handleFocus = () => {
      if (authState.isAuthenticated) {
        verifyIfStale();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [authState.isAuthenticated, verifyIfStale]);

  // Memorisation de la valeur du contexte pour eviter les re-renders
  // inutiles des consumers quand AuthProvider re-render sans changement d'etat
  const contextValue = useMemo(() => ({
    ...authState,
    setAuthInfo,
    logout,
  }), [authState, setAuthInfo, logout]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
